import { defer, from, Observable, of } from 'rxjs';
import { finalize, switchMap, tap } from 'rxjs/operators';
import { LoadingController } from '@ionic/angular';
import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class LoadingService {
  private counter = 0;
  private loadingOverlay?: HTMLIonLoadingElement;

  constructor(private loadingController: LoadingController) {}

  public show(): Observable<HTMLIonLoadingElement> {
    this.counter++;

    if (this.loadingOverlay) {
      return of(this.loadingOverlay);
    }

    return from(
      this.loadingController.create({
        backdropDismiss: false,
      })
    ).pipe(
      tap((overlay) => {
        this.loadingOverlay = overlay;
      }),
      switchMap((overlay) =>
        from(
          overlay.present().then(() => {
            return overlay;
          })
        )
      )
    );
  }

  public hide(force = false): Observable<boolean> {
    this.counter = force ? 0 : Math.max(0, this.counter - 1);

    if (this.counter === 0 && this.loadingOverlay) {
      const toDismiss = this.loadingOverlay;
      this.loadingOverlay = undefined;

      return from(toDismiss.dismiss()).pipe(switchMap(() => of(true)));
    }

    return of(false);
  }

  public withLoading<T>(): (source: Observable<T>) => Observable<T> {
    return (source: Observable<T>) =>
      defer(() => {
        return this.show().pipe(
          switchMap(() => source),
          finalize(() => {
            this.hide().pipe(untilDestroyed(this)).subscribe();
          })
        );
      });
  }
}
